import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Mailbox } from '../types/models/Mailbox';
import type { ApiResponseMailboxes } from '../types/api/ApiResponseMailboxes';

interface UseMailboxesReturn {
  mailboxes: Mailbox[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useMailboxes = (mailDomainId: number | undefined): UseMailboxesReturn => {
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchMailboxes = useCallback(async () => {
    if (!mailDomainId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/emails/mailboxes?mail_domain_id=${mailDomainId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseMailboxes;
      if (data.status === 'success' && data.data?.mailboxes) {
        setMailboxes(data.data.mailboxes);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load mailboxes');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching mailboxes');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, mailDomainId]);

  useEffect(() => {
    if (auth.isAuthenticated && mailDomainId) {
      fetchMailboxes();
    }
  }, [auth.isAuthenticated, mailDomainId, fetchMailboxes]);

  return { mailboxes, loading, error, refetch: fetchMailboxes };
};
